import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileDropZone } from "../../../components/UIToolkit/Inputs/FileDropZone";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type { PbjManualImportPreview, PbjManualImportRow, PbjManualImportRowStatus } from "../../../utils/models";
import { PBJ_MODEL_QUERY_KEY } from "../hooks/usePbjModel";

type Props = {
	open: boolean;
	onClose: () => void;
	departmentId: string;
};

type Step = "upload" | "preview";

// Status → theme chip color (no hardcoded hex — MUI palette tokens): additive/new=success, edit=info,
// removal=error, no-op=neutral.
const STATUS_COLOR: Record<PbjManualImportRowStatus, "success" | "info" | "error" | "default"> = {
	new: "success",
	updated: "info",
	removed: "error",
	unchanged: "default",
};

const formatDate = (iso: string): string => {
	const dt = DateTime.fromISO(iso);
	return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : iso;
};

const SummaryTile: FC<{ label: string; value: number | string }> = ({ label, value }) => (
	<Box sx={{ flex: "1 1 90px", p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1.5, textAlign: "center" }}>
		<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
			{value}
		</Typography>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
	</Box>
);

/**
 * PBJ Audit → "Import manual hours": upload a punch-less admin-submission file (net hours per employee/day),
 * preview the scoped diff (insert/update/remove/unchanged) against existing manual rows, then commit. Two steps
 * in one dialog: the file is dry-run on "Preview" and re-sent on "Confirm". An unrouted facility or unmapped job
 * blocks the commit (surfaced as block-severity warnings); an overlap with HRIS punch hours is a non-blocking warning.
 */
export const ImportManualHoursDialog: FC<Props> = ({ open, onClose, departmentId }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const queryClient = useQueryClient();
	const [step, setStep] = useState<Step>("upload");
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<PbjManualImportPreview | null>(null);
	const [isRunning, setIsRunning] = useState(false);

	const reset = useCallback(() => {
		setStep("upload");
		setFile(null);
		setPreview(null);
	}, []);

	const handleClose = useCallback(() => {
		if (isRunning) return;
		reset();
		onClose();
	}, [isRunning, reset, onClose]);

	const handlePreview = useCallback(async () => {
		if (!file || isRunning) return;
		setIsRunning(true);
		try {
			const res = await backendService.importManualHours({ departmentId, file, dryRun: true });
			setPreview(res.data);
			setStep("preview");
		} catch {
			showSnackbar(t("pbj_model.manual_import.failed"), "error");
		} finally {
			setIsRunning(false);
		}
	}, [file, isRunning, departmentId, showSnackbar, t]);

	const handleConfirm = useCallback(async () => {
		if (!file || isRunning || preview?.blocked) return;
		setIsRunning(true);
		try {
			const res = await backendService.importManualHours({ departmentId, file, dryRun: false });
			setPreview(res.data);
			if (res.data.committed) {
				// Refetch all PBJ audit/submission views (staffing hours, employees, role summary, …) so the
				// grids reflect the just-committed rows without a manual page refresh.
				await queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "audit"] });
				showSnackbar(t("pbj_model.manual_import.success"), "success");
				handleClose();
			} else {
				showSnackbar(t("pbj_model.manual_import.failed"), "error");
			}
		} catch {
			showSnackbar(t("pbj_model.manual_import.failed"), "error");
		} finally {
			setIsRunning(false);
		}
	}, [file, isRunning, preview, departmentId, showSnackbar, t, handleClose, queryClient]);

	const columns: GridColDef<PbjManualImportRow>[] = useMemo(
		() => [
			{
				field: "status",
				headerName: t("pbj_model.manual_import.cols.status"),
				minWidth: 110,
				renderCell: ({ value }) => (
					<Chip size="small" color={STATUS_COLOR[value as PbjManualImportRowStatus]} label={t(`pbj_model.manual_import.status.${value}`)} />
				),
			},
			{ field: "name", headerName: t("pbj_model.manual_import.cols.name"), minWidth: 160, flex: 1 },
			{ field: "pbjEmployeeId", headerName: t("pbj_model.manual_import.cols.id"), minWidth: 110, flex: 1 },
			{ field: "date", headerName: t("pbj_model.manual_import.cols.date"), minWidth: 120, valueFormatter: (v: string) => formatDate(v) },
			{ field: "jobDescription", headerName: t("pbj_model.manual_import.cols.job"), minWidth: 120, flex: 1 },
			{ field: "facility", headerName: t("pbj_model.manual_import.cols.facility"), minWidth: 120, flex: 1 },
			{
				field: "newHours",
				headerName: t("pbj_model.manual_import.cols.hours"),
				minWidth: 120,
				valueGetter: (_v, row) => (row.oldHours != null && row.oldHours !== row.newHours ? `${row.oldHours} → ${row.newHours}` : row.newHours),
			},
		],
		[t],
	);

	const blockers = preview?.warnings.filter((w) => w.severity === "block") ?? [];
	const softWarnings = preview?.warnings.filter((w) => w.severity === "warn") ?? [];

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth data-testid="pbj-import-manual-hours-dialog">
			<DialogTitle sx={{ fontWeight: 700 }}>{t("pbj_model.manual_import.title")}</DialogTitle>
			<DialogContent>
				{step === "upload" ? (
					<Stack spacing={2.5} sx={{ pt: 1 }}>
						<Typography variant="body2" color="text.secondary">
							{t("pbj_model.manual_import.description")}
						</Typography>
						<Alert severity="info">{t("pbj_model.manual_import.columns_hint")}</Alert>
						<FileDropZone
							accept=".xlsx,.xls,.csv"
							hint={t("pbj_model.manual_import.file_hint")}
							onFiles={(files) => setFile(files[0] ?? null)}
							dataTestId="pbj-manual-import-dropzone"
						/>
						{file && (
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{file.name}
							</Typography>
						)}
					</Stack>
				) : (
					preview && (
						<Stack spacing={2} sx={{ pt: 1 }}>
							<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
								<SummaryTile label={t("pbj_model.manual_import.summary.new")} value={preview.summary.inserted} />
								<SummaryTile label={t("pbj_model.manual_import.summary.updated")} value={preview.summary.updated} />
								<SummaryTile label={t("pbj_model.manual_import.summary.removed")} value={preview.summary.deleted} />
								<SummaryTile label={t("pbj_model.manual_import.summary.unchanged")} value={preview.summary.unchanged} />
								<SummaryTile label={t("pbj_model.manual_import.summary.hours_added")} value={preview.summary.hoursAdded} />
								<SummaryTile label={t("pbj_model.manual_import.summary.hours_removed")} value={preview.summary.hoursRemoved} />
							</Box>

							{blockers.map((w) => (
								<Alert key={`${w.code}-${w.message}`} severity="error">
									{w.message}
								</Alert>
							))}
							{softWarnings.map((w) => (
								<Alert key={`${w.code}-${w.message}`} severity="warning">
									{w.message}
								</Alert>
							))}

							{preview.newEmployees.length > 0 && (
								<Alert severity="info" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
									<Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
										{t("pbj_model.manual_import.new_employees_title", { count: preview.newEmployees.length })}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{preview.newEmployees.map((e) => `${e.name} (${e.pbjEmployeeId})`).join(", ")}
									</Typography>
								</Alert>
							)}

							<Box sx={{ height: 420 }}>
								<SystemDataGrid<PbjManualImportRow>
									rows={preview.rows}
									columns={columns}
									getRowId={(r) => `${r.departmentId}-${r.pbjEmployeeId}-${r.date}-${r.jobDescription}`}
									searchableColumns={["name", "pbjEmployeeId", "jobDescription", "facility"]}
									defaultPageSize={25}
									containedHeight
									dataTestId="pbj-manual-import-preview-table"
								/>
							</Box>
						</Stack>
					)
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={isRunning} sx={{ textTransform: "none" }}>
					{t("pbj_model.manual_import.cancel")}
				</Button>
				{step === "preview" && (
					<Button onClick={() => setStep("upload")} disabled={isRunning} sx={{ textTransform: "none" }}>
						{t("pbj_model.manual_import.back")}
					</Button>
				)}
				{step === "upload" ? (
					<Button
						onClick={handlePreview}
						variant="contained"
						disabled={!file || isRunning}
						sx={{ textTransform: "none", fontWeight: 600 }}
						data-testid="pbj-manual-import-preview"
					>
						{isRunning ? t("pbj_model.manual_import.running") : t("pbj_model.manual_import.preview")}
					</Button>
				) : (
					<Button
						onClick={handleConfirm}
						variant="contained"
						disabled={isRunning || (preview?.blocked ?? true)}
						sx={{ textTransform: "none", fontWeight: 600 }}
						data-testid="pbj-manual-import-confirm"
					>
						{isRunning ? t("pbj_model.manual_import.running") : t("pbj_model.manual_import.confirm")}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
